import React, { useState } from 'react';
import styled from 'styled-components';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import TrueImpactTextfield from 'shared/design-system/components/TrueImpactTextfield';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWandMagicSparkles, faSparkles } from '@fortawesome/pro-light-svg-icons';
import { MenuItem, Select } from '@mui/material';
import { Card, ErrorBanner, EmptyState, CharCount, ScoreBar } from './shared';
import FeedbackForm from './FeedbackForm';
import { postMatch, postMatchByProfile, MatchResponse, ProfileMatchResponse } from './api';
import {
  MAX_DESCRIPTION,
  MAX_BEST_MATCHES,
  DEFAULT_BEST_MATCHES,
} from './constants';

const StyledCard = styled(Card)`
  .form-row {
    display: flex;
    align-items: flex-end;
    gap: ${SPACERS.small};
    margin-top: ${SPACERS.small};
    flex-wrap: wrap;
  }

  .field-meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 4px 0;
  }

  .result-row {
    display: flex;
    gap: ${SPACERS.small};
    align-items: flex-start;
    padding: ${SPACERS.small} 0;
    border-bottom: 1px solid ${COLORS.greyLight};
    &:last-child {
      border-bottom: none;
    }
    .rank {
      flex-shrink: 0;
      width: 28px;
      height: 28px;
      border-radius: 50%;
      background: ${COLORS.ctaPrimaryBackground};
      color: ${COLORS.ctaPrimary};
      font-weight: 700;
      font-size: 13px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .body {
      flex: 1;
      min-width: 0;
    }
  }

  .feedback-section {
    margin-top: ${SPACERS.medium};
    padding-top: ${SPACERS.medium};
    border-top: 1px solid ${COLORS.greyLight};
  }
`;

const MatchTab: React.FC = () => {
  const [description, setDescription] = useState('');
  const [n, setN] = useState(DEFAULT_BEST_MATCHES);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<MatchResponse | null>(null);

  const [profileAccountId, setProfileAccountId] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileResult, setProfileResult] = useState<ProfileMatchResponse | null>(null);

  const runMatch = async () => {
    const text = description.trim();
    if (!text) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await postMatch(text, n);
      setResult(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Match request failed.');
    } finally {
      setLoading(false);
    }
  };

  const runProfileMatch = async () => {
    setProfileLoading(true);
    setProfileError(null);
    setProfileResult(null);
    try {
      const parsed = parseInt(profileAccountId.trim(), 10);
      const accountId = profileAccountId.trim() && !isNaN(parsed) ? parsed : undefined;
      const res = await postMatchByProfile(n, accountId);
      if (!res.profile_query) {
        setProfileError('No NTEE profile data found for this account.');
      } else {
        setProfileResult(res);
      }
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : 'Profile match request failed.');
    } finally {
      setProfileLoading(false);
    }
  };

  const renderMatches = (matchResult: MatchResponse | ProfileMatchResponse) => (
    <>
      <TrueImpactText
        variant="body-small"
        color="grayDark"
        style={{ display: 'block', marginTop: 4 }}
      >
        Match ID:{' '}
        <span style={{ fontFamily: 'monospace' }}>{matchResult.match_id}</span>
      </TrueImpactText>
      {matchResult.matches.length === 0 ? (
        <EmptyState style={{ marginTop: SPACERS.small }}>
          <div className="icon">
            <FontAwesomeIcon icon={faWandMagicSparkles} />
          </div>
          <TrueImpactText>No matches found.</TrueImpactText>
        </EmptyState>
      ) : (
        matchResult.matches.map(m => (
          <div className="result-row" key={m.template_id}>
            <div className="rank">{m.rank}</div>
            <div className="body">
              <TrueImpactText variant="body-small-bold">
                {m.name || 'Unnamed template'}
              </TrueImpactText>
              <TrueImpactText
                variant="body-small"
                color="grayDark"
                style={{ display: 'block', margin: '2px 0' }}
              >
                {m.description}
              </TrueImpactText>
              <ScoreBar score={m.score} />
            </div>
          </div>
        ))
      )}
      {matchResult.match_id != null && (
        <div className="feedback-section">
          <FeedbackForm key={matchResult.match_id} matchId={matchResult.match_id} matches={matchResult.matches} />
        </div>
      )}
    </>
  );

  return (
    <>
      <StyledCard>
        <TrueImpactText variant="heading-subheading">
          Auto-suggest from NPO profile
        </TrueImpactText>
        <TrueImpactText
          variant="body-small"
          color="grayDark"
          style={{ display: 'block', marginTop: 4, marginBottom: SPACERS.small }}
        >
          Uses the account's NTEE category and focus from their NPO profile to suggest templates automatically.
        </TrueImpactText>
        <div className="form-row">
          <div style={{ width: 200 }}>
            <TrueImpactText
              variant="body-small-bold"
              style={{ display: 'block', marginBottom: 4 }}
            >
              Account ID (leave blank to use your own)
            </TrueImpactText>
            <TrueImpactTextfield
              placeholder="e.g. 1234"
              value={profileAccountId}
              onChange={v => setProfileAccountId(v)}
            />
          </div>
          <TrueImpactButton
            variant="primary-large"
            disabled={profileLoading}
            onClick={runProfileMatch}
            style={{ padding: '10px 20px', fontSize: 14 }}
          >
            <FontAwesomeIcon icon={faSparkles} style={{ marginRight: 6 }} />
            {profileLoading ? 'Matching…' : 'Auto-suggest'}
          </TrueImpactButton>
        </div>
        {profileError && (
          <ErrorBanner style={{ marginTop: SPACERS.small }}>
            {profileError}
          </ErrorBanner>
        )}
      </StyledCard>

      {profileLoading && (
        <Card>
          <TrueImpactLoading />
        </Card>
      )}

      {profileResult && (
        <StyledCard>
          <TrueImpactText variant="heading-subheading">Profile-based Results</TrueImpactText>
          <TrueImpactText
            variant="body-small"
            color="grayDark"
            style={{ display: 'block', marginTop: 4, marginBottom: SPACERS.small }}
          >
            Query built from profile:{' '}
            <span style={{ fontFamily: 'monospace', fontStyle: 'italic' }}>
              {profileResult.profile_query}
            </span>
          </TrueImpactText>
          {renderMatches(profileResult)}
        </StyledCard>
      )}

      <StyledCard>
        <TrueImpactText variant="heading-subheading">
          Describe what you're looking for
        </TrueImpactText>
        <TrueImpactText
          variant="body-small"
          color="grayDark"
          style={{
            display: 'block',
            marginTop: 4,
            marginBottom: SPACERS.small,
          }}
        >
          The agent will return the best-matching templates, ranked by
          relevance.
        </TrueImpactText>
        <TrueImpactTextfield
          isTextarea
          height={120}
          placeholder="e.g. landing page for a coffee shop"
          value={description}
          maxLength={MAX_DESCRIPTION}
          onChange={v => setDescription(v)}
        />
        <div className="field-meta">
          <CharCount over={description.length >= MAX_DESCRIPTION}>
            {description.length} / {MAX_DESCRIPTION}
          </CharCount>
        </div>
        <div className="form-row">
          <div>
            <TrueImpactText
              variant="body-small-bold"
              style={{ display: 'block', marginBottom: 4 }}
            >
              Matches to return
            </TrueImpactText>
            <Select
              size="small"
              value={n}
              onChange={e => setN(Number(e.target.value))}
              style={{ minWidth: 80, fontSize: 14 }}
            >
              {Array.from({ length: MAX_BEST_MATCHES }, (_, i) => i + 1).map(
                i => (
                  <MenuItem key={i} value={i}>
                    {i}
                  </MenuItem>
                ),
              )}
            </Select>
          </div>
          <TrueImpactButton
            variant="primary-large"
            disabled={loading || !description.trim()}
            onClick={runMatch}
            style={{ padding: '10px 20px', fontSize: 14 }}
          >
            {loading ? 'Matching…' : 'Find Matches'}
          </TrueImpactButton>
        </div>
        {error && (
          <ErrorBanner style={{ marginTop: SPACERS.small }}>
            {error}
          </ErrorBanner>
        )}
      </StyledCard>

      {loading && (
        <Card>
          <TrueImpactLoading />
        </Card>
      )}

      {result && (
        <StyledCard>
          <TrueImpactText variant="heading-subheading">Results</TrueImpactText>
          {renderMatches(result)}
        </StyledCard>
      )}
    </>
  );
};

export default MatchTab;
